import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, KeyRound, ShieldCheck } from 'lucide-react';
import { useCountdownNow } from '../../../application/hooks/useCountdownNow';
import { changePasswordFailureOf, useChangePassword } from '../../../application/hooks/useChangePassword';
import { useTranslation } from '../../../application/hooks/useTranslation';
import { useAuthStore } from '../../../application/stores/authStore';
import { checkPasswordPolicy, passwordStrength } from '../../../domain/validators/passwordPolicy';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

type Field = 'current' | 'next' | 'confirm';

/** An i18n key plus its variables; translated at render so a locale switch applies to a shown error. */
interface Notice {
  key: string;
  vars?: Record<string, string | number>;
  retryable?: boolean;
}

const RULES = [
  { rule: 'LENGTH', label: 'auth.forgot.password.ruleLength' },
  { rule: 'LETTER', label: 'auth.forgot.password.ruleLetter' },
  { rule: 'DIGIT', label: 'auth.forgot.password.ruleDigit' },
] as const;

const FIELD_LABEL: Record<Field, string> = {
  current: 'profile.password.current',
  next: 'profile.password.new',
  confirm: 'profile.password.confirm',
};

/**
 * Profile → change password (E33). The member is already signed in, so there is no OTP: current password, new
 * password, confirmation. Passwords exist only in this component's state and the mutation call (never storage, a
 * query key or a log) and are dropped on success. On success the backend keeps this session and returns a fresh
 * access token (stored by the hook); other devices are signed out.
 */
export function ChangePasswordCard({ phone }: { phone: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const change = useChangePassword();
  const [now, setNow] = useCountdownNow();

  const [values, setValues] = useState<Record<Field, string>>({ current: '', next: '', confirm: '' });
  const [shown, setShown] = useState<Record<Field, boolean>>({ current: false, next: false, confirm: false });
  const [touched, setTouched] = useState<Record<Field, boolean>>({ current: false, next: false, confirm: false });
  const [submitted, setSubmitted] = useState(false);
  const [wrongCurrent, setWrongCurrent] = useState(false); // server said the current password is wrong
  const [serverSame, setServerSame] = useState(false); // server said new == current
  const [serverPolicy, setServerPolicy] = useState(false); // server rejected the policy (client rules drifted)
  const [notice, setNotice] = useState<Notice | null>(null);
  const [success, setSuccess] = useState(false);
  const [locked, setLocked] = useState(false); // ACCOUNT_SUSPENDED: read-only
  const [blockedUntil, setBlockedUntil] = useState(0);
  const inFlight = useRef(false); // holds even before React re-renders
  const refs = {
    current: useRef<HTMLInputElement>(null),
    next: useRef<HTMLInputElement>(null),
    confirm: useRef<HTMLInputElement>(null),
  };
  const [focusTarget, setFocusTarget] = useState<Field | null>(null);

  useEffect(() => {
    if (!focusTarget) return;
    refs[focusTarget].current?.focus();
    setFocusTarget(null);
  }, [focusTarget]);

  const { current, next, confirm } = values;
  const violations = checkPasswordPolicy(next, phone);
  const strength = passwordStrength(next, phone);
  const blockedIn = Math.max(0, Math.ceil((blockedUntil - now) / 1000));
  const waiting = blockedIn > 0;
  const pending = change.isPending;

  // Client-side field errors, shown after blur or a submit attempt.
  const show = (f: Field) => submitted || touched[f];
  const currentError =
    show('current') && !current
      ? t('profile.password.error.currentRequired')
      : wrongCurrent
        ? t('profile.password.error.currentWrong')
        : undefined;
  const sameClient = next.length > 0 && next === current;
  const nextError =
    show('next') && !next
      ? t('profile.password.error.newRequired')
      : sameClient || serverSame
        ? t('profile.password.error.same')
        : serverPolicy || (show('next') && violations.length > 0)
          ? t('profile.password.error.policy')
          : undefined;
  const confirmError =
    show('confirm') && !confirm
      ? t('profile.password.error.confirmRequired')
      : confirm.length > 0 && next !== confirm && (show('confirm') || confirm.length >= next.length)
        ? t('profile.password.error.mismatch')
        : undefined;

  const set = (f: Field, v: string) => {
    setValues((s) => ({ ...s, [f]: v }));
    setSuccess(false);
    if (f === 'current') setWrongCurrent(false);
    if (f === 'next' || f === 'current') setServerSame(false);
    if (f === 'next') setServerPolicy(false);
  };
  const blur = (f: Field) => setTouched((s) => ({ ...s, [f]: true }));

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (inFlight.current || locked || waiting) return;
    setSubmitted(true);
    setNotice(null);
    const invalid: Field | null = !current
      ? 'current'
      : !next || sameClient || violations.length > 0
        ? 'next'
        : !confirm || next !== confirm
          ? 'confirm'
          : null;
    if (invalid) {
      setFocusTarget(invalid);
      return;
    }
    inFlight.current = true;
    change.mutate(
      { currentPassword: current, newPassword: next },
      {
        onSuccess: () => {
          setValues({ current: '', next: '', confirm: '' });
          setShown({ current: false, next: false, confirm: false });
          setTouched({ current: false, next: false, confirm: false });
          setSubmitted(false);
          setWrongCurrent(false);
          setServerSame(false);
          setServerPolicy(false);
          setSuccess(true);
        },
        onError: (error) => {
          const f = changePasswordFailureOf(error);
          if (f.status === 401) {
            // Session is gone (the client already tried a silent refresh): the existing logout path.
            useAuthStore.getState().clearSession();
            queryClient.clear();
            navigate('/login', { replace: true });
          } else if (f.code === 'CURRENT_PASSWORD_INVALID') {
            setWrongCurrent(true);
            setFocusTarget('current');
          } else if (f.code === 'NEW_PASSWORD_SAME_AS_CURRENT') {
            setServerSame(true);
            setFocusTarget('next');
          } else if (f.code === 'PASSWORD_POLICY') {
            setServerPolicy(true);
            setFocusTarget('next');
          } else if (f.code === 'RATE_LIMITED' || f.status === 429) {
            const seconds = f.retryAfterSeconds ?? 60;
            const at = Date.now();
            setNow(at);
            setBlockedUntil(at + seconds * 1000);
            setNotice({ key: 'profile.password.error.rateWait', vars: { minutes: Math.max(1, Math.ceil(seconds / 60)) } });
          } else if (f.code === 'ACCOUNT_SUSPENDED') {
            setLocked(true);
            setNotice({ key: 'profile.password.error.suspended' });
          } else if (f.code === 'STALE_UPDATE') {
            setNotice({ key: 'profile.password.error.stale' });
          } else if (f.status === undefined) {
            setNotice({ key: 'profile.password.error.network', retryable: true });
          } else {
            setNotice({ key: 'profile.password.error.generic', retryable: f.status >= 500 });
          }
        },
        onSettled: () => {
          inFlight.current = false;
        },
      },
    );
  };

  const readOnly = locked;
  const row = (f: Field, props: { autoComplete: string; error?: string; describedBy?: string; testId: string }) => {
    const id = `pw-${f}`;
    const label = t(FIELD_LABEL[f]);
    const describedBy = [props.describedBy, props.error ? `${id}-error` : null].filter(Boolean).join(' ') || undefined;
    return (
      <div className="pw-field-row">
        <Input
          id={id}
          ref={refs[f]}
          label={label}
          type={shown[f] ? 'text' : 'password'}
          autoComplete={props.autoComplete}
          value={values[f]}
          onChange={(e) => set(f, e.target.value)}
          onBlur={() => blur(f)}
          error={props.error}
          aria-describedby={describedBy}
          aria-required
          readOnly={readOnly}
          data-testid={props.testId}
        />
        <button
          type="button"
          className="pw-toggle"
          aria-pressed={shown[f]}
          aria-label={t(shown[f] ? 'profile.password.hideField' : 'profile.password.showField', { field: label })}
          onClick={() => setShown((s) => ({ ...s, [f]: !s[f] }))}
          data-testid={`${props.testId}-toggle`}
        >
          {shown[f] ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
        </button>
      </div>
    );
  };

  const noticeText = notice && (notice.key !== 'profile.password.error.rateWait' || waiting) ? t(notice.key, notice.vars) : null;

  return (
    <section className="profile-card" data-testid="change-password-card">
      <div className="profile-card-header">
        <div className="profile-card-header-main">
          <div className="profile-card-icon-tile profile-icon-slate">
            <KeyRound size={18} aria-hidden />
          </div>
          <div className="profile-card-title-wrap">
            <h3 className="profile-card-title">{t('profile.password.cardTitle')}</h3>
            <p className="profile-card-subtitle">{t('profile.passwordSubtitle')}</p>
          </div>
        </div>
        <div className="profile-card-header-badge">
          <span className="profile-status-pill profile-pill-slate">
            <ShieldCheck size={12} aria-hidden />
            {t('profile.pillSecured')}
          </span>
        </div>
      </div>

      <div className="profile-card-body">
        <p className="muted pw-intro">{t('profile.password.cardBody')}</p>
        <form noValidate onSubmit={submit} data-testid="pw-form" aria-busy={pending}>
          {row('current', { autoComplete: 'current-password', error: currentError, testId: 'pw-current' })}
          {row('next', { autoComplete: 'new-password', error: nextError, describedBy: 'pw-rules', testId: 'pw-new' })}
          <ul className="pw-rules" id="pw-rules" aria-live="polite" data-testid="pw-rules">
            {RULES.map((r) => (
              <li key={r.rule} data-ok={next.length > 0 && !violations.includes(r.rule)} data-testid={`pw-rule-${r.rule}`}>
                {t(r.label)}
              </li>
            ))}
            <li data-ok={next.length > 0 && !violations.includes('NOT_PHONE')} data-testid="pw-rule-NOT_PHONE">
              {t('profile.password.ruleNotPhone')}
            </li>
            <li data-ok={next.length > 0 && !sameClient && !serverSame} data-testid="pw-rule-DIFFERENT">
              {t('profile.password.ruleDifferent')}
            </li>
          </ul>
          <div className="pw-strength" data-level={strength} data-testid="pw-strength">
            <span className="pw-strength-bar" aria-hidden />
            <span className="pw-strength-label">{t(`profile.password.strength.${strength}`)}</span>
          </div>
          {row('confirm', { autoComplete: 'new-password', error: confirmError, testId: 'pw-confirm' })}

          {noticeText && (
            <p className="text-danger pw-notice" role="alert" data-testid="pw-error">
              {noticeText}
              {notice?.retryable && (
                <>
                  {' '}
                  <button type="button" className="pw-link" onClick={() => submit()} disabled={pending} data-testid="pw-retry">
                    {t('profile.password.retry')}
                  </button>
                </>
              )}
            </p>
          )}
          <div aria-live="polite" data-testid="pw-live">
            {success && (
              <p className="pw-success" role="status" data-testid="pw-done">
                {t('profile.password.success')}
              </p>
            )}
          </div>
          <div className="profile-card-footer">
            <Button type="submit" loading={pending} disabled={readOnly || waiting} data-testid="pw-submit">
              {waiting ? t('profile.password.waitIn', { seconds: blockedIn }) : t('profile.password.submit')}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}
